/** PASUL «VOLUMETRIA» (Cătălin, 8.10.2026; `positioning_plan/docs/SCHITA-VOLUMETRIE.md`).
 *
 *  Între temă și fațadă, omul vede terenul și poate DESENA volume aproximative
 *  — bare, L-uri, U-uri, cu lungime și lățime, ca să vadă distanțele dintre
 *  clădiri —, sau lasă motorul să propună, ca până acum. Schița e o INSPIRAȚIE:
 *  motorul ia din ea orientările, numărul de clădiri, formele și pozițiile, dar
 *  optimizează pe CUT; prima soluție va fi cea mai asemănătoare cu schița.
 *
 *  DOUĂ MODURI, și fără schiță nimic nu se schimbă: `tema.schita` rămâne `null`
 *  și generarea e exact cea de azi.
 *
 *  PÂNZA e în METRI, în reperul lotului (`contur_stereo70`, perechi [N, E]), cu
 *  nordul în sus: x = E, y = −N. Geometria e în `volumetrie/geometrie.ts`
 *  (pură, testată); aici e doar desenul și mâna.
 *
 *  CIFRELE DE PE ECRAN vin de la motor (`estimeazaSchita`), cu regulile lui —
 *  amprenta la fața finită, deducerile de trepte și terase, zona edificabilă,
 *  distanța cerută între corpuri — și sunt marcate «estimat»: compunerea din
 *  module poate scurta un corp, terasele reale pot fi mai adânci. */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pasi } from "./Pasi";
import { citesteLocalizare, estimeazaSchita, salveazaLocalizare, sugestiiVolumetrie, TEMA_GOALA } from "./dosarclient";
import type { EstimareSchita, FormaVolum, Localizare, SchitaVolumetrie, SugestieVolumetrie,
              VolumSchita } from "./dosarclient";
import * as I from "../components/icons";
import { BandaDoarCitire, useRolProiect } from "../components/rol";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import * as G from "./volumetrie/geometrie";
import type { Pct } from "./volumetrie/geometrie";
import "./volumetrie.css";

const FORME: FormaVolum[] = ["bara", "L", "U", "C"];
/** câte metri de aer în jurul lotului, pe pânză */
const MARGINE_M = 25;
/** cât așteaptă estimarea după ultima mișcare */
const INTARZIERE_ESTIMARE_MS = 300;

type Mod = "motor" | "desen";
type Tragere =
  | { fel: "muta"; id: string; ultim: Pct; miscat: boolean }
  | { fel: "maner"; id: string; maner: G.Maner }
  | { fel: "pan"; ultim: [number, number] };

/** lume [N, E] → pânză (x, y) în metri, nordul în sus */
const laPanza = (p: Pct): [number, number] => [p[1], -p[0]];
const puncte = (poly: Pct[]) => poly.map((p) => laPanza(p).join(",")).join(" ");

export default function Volumetrie() {
  const t = useT();
  const { projectId } = useParams();
  const pid = projectId as string;
  const nav = useNavigate();
  const { potScrie } = useRolProiect();
  const [loc, setLoc] = useState<Localizare | null>(null);
  const [gata, setGata] = useState(false);
  const [err, setErr] = useState("");

  const [volume, setVolume] = useState<VolumSchita[]>([]);
  const [mod, setMod] = useState<Mod>("motor");
  const [sel, setSel] = useState<string | null>(null);
  const [trecut, setTrecut] = useState<VolumSchita[][]>([]);
  const [viitor, setViitor] = useState<VolumSchita[][]>([]);
  const [est, setEst] = useState<EstimareSchita | null>(null);
  const [estErr, setEstErr] = useState("");
  const [sugestii, setSugestii] = useState<{ lista: SugestieVolumetrie[]; secunde: number } | null>(null);
  const [cautSugestii, setCautSugestii] = useState(false);
  const [vedere, setVedere] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [pxPerM, setPxPerM] = useState(4);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const tragere = useRef<Tragere | null>(null);
  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  useEffect(() => {
    citesteLocalizare(pid)
      .then((l) => {
        setLoc(l);
        const s = l?.tema?.schita;
        if (s?.volume?.length) {
          // conturul se reface din parametri: ce s-a salvat putea fi o sugestie
          // a motorului, cu conturul lui exact; parametrii sunt ai ecranului
          setVolume(s.volume.map((v) => ({ ...v, poly: v.poly?.length >= 3 ? v.poly : G.poligonVolum(v) })));
          setMod("desen");
        }
      })
      .catch((e) => setErr(String(e)))
      .finally(() => setGata(true));
  }, [pid]);

  const contur = useMemo<Pct[] | null>(() => {
    const c = loc?.contur_stereo70;
    return c && c.length >= 3 ? c.map((p) => [p[0], p[1]] as Pct) : null;
  }, [loc?.contur_stereo70]);
  const areAcces = (loc?.zone_acces?.length ?? 0) > 0;

  // vederea inițială: lotul întreg, cu aer în jur
  useEffect(() => {
    if (!contur || vedere) return;
    const b = G.cutie(contur);
    setVedere({ x: b.e0 - MARGINE_M, y: -b.n1 - MARGINE_M,
                w: b.e1 - b.e0 + 2 * MARGINE_M, h: b.n1 - b.n0 + 2 * MARGINE_M });
  }, [contur, vedere]);
  // dublu-clic pe gol: lotul întreg la loc

  // câți pixeli are un metru pe ecran: textele și mânerele se țin în pixeli
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !vedere) return;
    const masoara = () => {
      const r = svg.getBoundingClientRect();
      setPxPerM(Math.min(r.width / vedere.w, r.height / vedere.h) || 4);
    };
    masoara();
    const ro = new ResizeObserver(masoara);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [vedere]);

  // --- salvarea --------------------------------------------------------------
  const salveaza = useCallback(async (vol: VolumSchita[], sursa: "om" | "sugestie" = "om") => {
    const schita: SchitaVolumetrie | null = vol.length ? { versiune: 1, volume: vol, sursa } : null;
    try {
      const l = await salveazaLocalizare(pid, { tema: { ...(loc?.tema || TEMA_GOALA), schita } });
      setLoc(l);
      setErr("");
    } catch (e) {
      setErr(String(e));
    }
  }, [pid, loc?.tema]);

  /** o schimbare încheiată: intră în istoric și se salvează */
  const comite = useCallback((vol: VolumSchita[], sursa: "om" | "sugestie" = "om") => {
    setTrecut((tr) => [...tr.slice(-30), volumeRef.current]);
    setViitor([]);
    setVolume(vol);
    void salveaza(vol, sursa);
  }, [salveaza]);

  const anuleaza = () => {
    if (!trecut.length) return;
    const ultim = trecut[trecut.length - 1];
    setTrecut(trecut.slice(0, -1));
    setViitor((v) => [volume, ...v]);
    setVolume(ultim);
    void salveaza(ultim);
  };
  const refa = () => {
    if (!viitor.length) return;
    const urm = viitor[0];
    setViitor(viitor.slice(1));
    setTrecut((tr) => [...tr, volume]);
    setVolume(urm);
    void salveaza(urm);
  };

  // --- estimarea, cu întârziere după ultima mișcare -----------------------------
  useEffect(() => {
    if (!contur || !areAcces || mod !== "desen") return;
    const h = window.setTimeout(() => {
      estimeazaSchita(pid, { versiune: 1, volume, sursa: "om" })
        .then((e) => { setEst(e); setEstErr(""); })
        .catch((e) => setEstErr(String(e?.message || e)));
    }, INTARZIERE_ESTIMARE_MS);
    return () => window.clearTimeout(h);
  }, [pid, contur, areAcces, mod, volume]);

  // --- unghiurile de lipire: laturile lotului și celelalte volume -----------------
  const candidatiUnghi = useCallback((faraId: string | null) => [
    ...(contur ? G.unghiuriLaturi(contur) : []),
    ...volumeRef.current.filter((v) => v.id !== faraId).map((v) => v.unghi_deg),
  ], [contur]);

  // --- mâna ---------------------------------------------------------------------
  const inLume = (ev: { clientX: number; clientY: number }): Pct => {
    const svg = svgRef.current!;
    const pt = svg.createSVGPoint();
    pt.x = ev.clientX; pt.y = ev.clientY;
    const q = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return [-q.y, q.x];
  };

  const laApasare = (ev: React.PointerEvent<SVGSVGElement>) => {
    if (!potScrie || mod !== "desen") {
      tragere.current = { fel: "pan", ultim: [ev.clientX, ev.clientY] };
      (ev.target as Element).setPointerCapture?.(ev.pointerId);
      return;
    }
    const el = (ev.target as Element).closest("[data-vol]") as HTMLElement | null;
    const p = inLume(ev);
    if (el?.dataset.maner != null && el.dataset.vol) {
      const v = volume.find((x) => x.id === el.dataset.vol)!;
      const m = G.manere(v)[Number(el.dataset.maner)];
      tragere.current = { fel: "maner", id: v.id, maner: m };
    } else if (el?.dataset.vol) {
      setSel(el.dataset.vol);
      tragere.current = { fel: "muta", id: el.dataset.vol, ultim: p, miscat: false };
    } else {
      setSel(null);
      tragere.current = { fel: "pan", ultim: [ev.clientX, ev.clientY] };
    }
    (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
  };

  const laMiscare = (ev: React.PointerEvent<SVGSVGElement>) => {
    const tr = tragere.current;
    if (!tr) return;
    if (tr.fel === "pan") {
      const dx = (ev.clientX - tr.ultim[0]) / pxPerM, dy = (ev.clientY - tr.ultim[1]) / pxPerM;
      tr.ultim = [ev.clientX, ev.clientY];
      setVedere((v) => v && inPreajma({ ...v, x: v.x - dx, y: v.y - dy }));
      return;
    }
    const p = inLume(ev);
    if (tr.fel === "muta") {
      const dn = p[0] - tr.ultim[0], de = p[1] - tr.ultim[1];
      tr.ultim = p; tr.miscat = true;
      setVolume((vol) => vol.map((v) => (v.id === tr.id ? G.muta(v, dn, de) : v)));
    } else {
      setVolume((vol) => vol.map((v) => (v.id === tr.id ? G.trageManer(v, tr.maner, p, candidatiUnghi(v.id)) : v)));
    }
  };

  const laRidicare = () => {
    const tr = tragere.current;
    tragere.current = null;
    if (!tr || tr.fel === "pan") return;
    if (tr.fel === "muta" && !tr.miscat) return;
    comite(volumeRef.current);
  };

  /** Vederea care încadrează lotul întreg, cu aer în jur. */
  const vedereIntreaga = useCallback(() => {
    if (!contur) return null;
    const b = G.cutie(contur);
    return { x: b.e0 - MARGINE_M, y: -b.n1 - MARGINE_M,
             w: b.e1 - b.e0 + 2 * MARGINE_M, h: b.n1 - b.n0 + 2 * MARGINE_M };
  }, [contur]);
  const incadreaza = () => { const v = vedereIntreaga(); if (v) setVedere(v); };

  // ROTIȚA, BLÂNDĂ ȘI CU LIMITE (Cătălin, 9.10.2026: «când fac zoom se pierde
  // imediat scena»). Un trackpad trimite zeci de evenimente mici pe un gest; cu
  // ×1,12 pe fiecare, lotul dispărea după o atingere. Acum factorul urmează
  // mărimea gestului (±8 % cel mult pe eveniment), mărirea stă între «lotul
  // întreg la jumătate» și ~1 px/cm, iar centrul vederii nu poate pleca mai
  // departe de lot decât o dată lățimea lui. Ascultătorul e nativ, ne-pasiv, ca
  // pagina să nu deruleze sub pânză.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const laRotita = (ev: WheelEvent) => {
      ev.preventDefault();
      const intreg = vedereIntreaga();
      if (!intreg) return;
      setVedere((v) => {
        if (!v) return v;
        const pas = Math.max(-80, Math.min(80, ev.deltaY)) / 1000;      // ±0,08
        let f = Math.exp(pas);
        const wMax = intreg.w * 2, wMin = Math.max(intreg.w / 40, 6);
        f = Math.max(wMin / v.w, Math.min(wMax / v.w, f));
        const r = svg.getBoundingClientRect();
        const fx = (ev.clientX - r.left) / r.width, fy = (ev.clientY - r.top) / r.height;
        return { x: v.x + v.w * fx * (1 - f), y: v.y + v.h * fy * (1 - f), w: v.w * f, h: v.h * f };
      });
    };
    svg.addEventListener("wheel", laRotita, { passive: false });
    return () => svg.removeEventListener("wheel", laRotita);
  }, [vedereIntreaga, vedere == null]);

  /** Vederea ținută în preajma lotului: centrul ei nu pleacă mai departe de o
   *  lățime a lotului. */
  const inPreajma = useCallback((v: { x: number; y: number; w: number; h: number }) => {
    const intreg = vedereIntreaga();
    if (!intreg) return v;
    const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
    const ix = intreg.x + intreg.w / 2, iy = intreg.y + intreg.h / 2;
    const dx = Math.max(-intreg.w, Math.min(intreg.w, cx - ix));
    const dy = Math.max(-intreg.h, Math.min(intreg.h, cy - iy));
    return { ...v, x: ix + dx - v.w / 2, y: iy + dy - v.h / 2 };
  }, [vedereIntreaga]);

  useEffect(() => {
    const laTasta = (ev: KeyboardEvent) => {
      if (!potScrie || mod !== "desen") return;
      if ((ev.key === "Delete" || ev.key === "Backspace") && sel && !(ev.target instanceof HTMLInputElement)) {
        ev.preventDefault();
        comite(volume.filter((v) => v.id !== sel)); setSel(null);
      } else if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "z") {
        ev.preventDefault();
        if (ev.shiftKey) refa(); else anuleaza();
      }
    };
    window.addEventListener("keydown", laTasta);
    return () => window.removeEventListener("keydown", laTasta);
  });

  // --- acțiuni --------------------------------------------------------------------
  const adauga = (forma: FormaVolum) => {
    if (!contur) return;
    // în centrul zonei edificabile (sau al lotului), paralel cu latura cea mai lungă
    const inel = est?.zona?.[0] ?? contur;
    const c = G.centroid(inel);
    const laturi = contur.map((a, i) => {
      const b = contur[(i + 1) % contur.length];
      return { l: Math.hypot(b[0] - a[0], b[1] - a[1]), u: (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180 / Math.PI + 180) % 180 };
    }).sort((a, b) => b.l - a.l);
    const v = G.volumNou(forma, c, laturi[0]?.u ?? 0);
    comite([...volume, v]); setSel(v.id);
  };
  const schimbaSel = (f: (v: VolumSchita) => VolumSchita) => {
    if (!sel) return;
    comite(volume.map((v) => (v.id === sel ? f(v) : v)));
  };
  const alegeMod = async (m: Mod) => {
    if (m === mod) return;
    if (m === "motor" && volume.length && !window.confirm(t("vol.sterge_tot") + "?")) return;
    setMod(m);
    if (m === "motor") { setVolume([]); setSel(null); await salveaza([]); }
  };
  const cereSugestii = async () => {
    setCautSugestii(true);
    try {
      const r = await sugestiiVolumetrie(pid);
      setSugestii({ lista: r.sugestii, secunde: r.secunde });
    } catch (e) {
      setErr(String(e));
    } finally {
      setCautSugestii(false);
    }
  };
  const incarcaSugestia = (s: SugestieVolumetrie) => {
    const vol = s.volume.map((v, i) => {
      const forma = (FORME as string[]).includes(v.forma) ? v.forma as FormaVolum : "bara";
      const p = { id: v.id || `s${i + 1}`, forma, centru: v.centru, unghi_deg: v.unghi_deg, latime_m: v.latime_m,
                  brate_m: v.brate_m, capat: (v.capat === -1 ? -1 : 1) as -1 | 1, parte: (v.parte === -1 ? -1 : 1) as -1 | 1 };
      return { ...p, poly: v.poly?.length >= 3 ? v.poly : G.poligonVolum(p) };
    });
    setSel(null);
    comite(vol, "sugestie");
  };

  // --- ce se vede ------------------------------------------------------------------
  const selectat = volume.find((v) => v.id === sel) ?? null;
  const gap = est?.gap_m ?? 0;
  const vecinul = useMemo(() => {
    if (!selectat) return null;
    let best: { d: number; p: Pct; q: Pct } | null = null;
    for (const v of volume) {
      if (v.id === selectat.id) continue;
      const a = G.apropiere(selectat.poly, v.poly);
      if (!best || a.d < best.d) best = a;
    }
    return best;
  }, [selectat, volume]);
  const limita = useMemo(() => (selectat && contur ? G.fataDeContur(selectat.poly, contur) : null), [selectat, contur]);
  const inAfara = new Set((est?.volume ?? []).filter((v) => !v.in_zona).map((v) => v.id));
  const preaAproape = new Set((est?.perechi_sub_distanta ?? []).flatMap(([a, b]) => [a, b]));

  if (!gata) {
    return <div className="gol" style={{ height: "100%" }}><span className="lbl">{t("loc.se_incarca")}</span></div>;
  }

  const fs = 12 / pxPerM;          // 12 px, în metri
  const rManer = 5 / pxPerM;

  return (
    <div className="tm vl">
      <div className="tm-cap">
        <Pasi pid={pid} activ="volumetrie" />
        <div className="sp" />
        <button className="btn btnp" style={{ height: 36, fontSize: 13 }}
                onClick={() => nav(`/p/${pid}/facade`)}>
          {t("vol.la_fatada")} <I.Sageata size={14} />
        </button>
      </div>

      <BandaDoarCitire />
      {err && <div className="err">{err}</div>}

      <div className="vl-col">
        <section className="card tm-card">
          <b className="tm-titlu">{t("vol.titlu")}</b>
          <p className="tm-sub">{t("vol.sub")}</p>
          <div className="vl-moduri">
            {(["motor", "desen"] as Mod[]).map((m) => (
              <button key={m} type="button" className={`tm-nivel${mod === m ? " on" : ""}`}
                      disabled={!potScrie} onClick={() => void alegeMod(m)}>
                <b>{t(`vol.mod.${m}` as Cheie)}</b>
                <span>{t(`vol.mod.${m}_sub` as Cheie)}</span>
              </button>
            ))}
          </div>
        </section>

        {mod === "desen" && (
          <section className="card tm-card vl-desen">
            {!contur ? <p className="note">{t("vol.fara_contur")}</p>
             : !areAcces ? <p className="note">{t("vol.fara_acces")}</p>
             : (
              <>
                <div className="vl-bara">
                  <span className="lbl">{t("vol.paleta")}</span>
                  {FORME.map((f) => (
                    <button key={f} type="button" className="btn" disabled={!potScrie} onClick={() => adauga(f)}>
                      <FormaMica forma={f} /> {t(`vol.forma.${f}` as Cheie)}
                    </button>
                  ))}
                  <span className="vl-sep" />
                  <button type="button" className="btn" disabled={!potScrie || cautSugestii}
                          title={t("vol.sugestii_titlu")} onClick={() => void cereSugestii()}>
                    {cautSugestii ? t("vol.sugestii_caut") : t("vol.sugestii")}
                  </button>
                  <div className="sp" />
                  <button type="button" className="btn" disabled={!trecut.length} title="⌘Z" onClick={anuleaza}>{t("vol.anuleaza")}</button>
                  <button type="button" className="btn" disabled={!viitor.length} title="⇧⌘Z" onClick={refa}>{t("vol.refa")}</button>
                  <button type="button" className="btn" title={t("vol.incadreaza_titlu")} onClick={incadreaza}>{t("vol.incadreaza")}</button>
                  {selectat && (
                    <>
                      {selectat.forma !== "bara" && (
                        <button type="button" className="btn" onClick={() => schimbaSel(G.oglindeste)}>{t("vol.oglindeste")}</button>)}
                      {selectat.forma === "L" && (
                        <button type="button" className="btn" onClick={() => schimbaSel(G.schimbaCapatul)}>{t("vol.capat")}</button>)}
                      <button type="button" className="btn" title="Delete"
                              onClick={() => { comite(volume.filter((v) => v.id !== sel)); setSel(null); }}>
                        <I.X size={12} /> {t("vol.sterge")}
                      </button>
                    </>
                  )}
                  {!selectat && volume.length > 0 && (
                    <button type="button" className="btn" onClick={() => { comite([]); setSel(null); }}>{t("vol.sterge_tot")}</button>)}
                </div>

                {sugestii && (
                  <div className="vl-sugestii">
                    <span className="lbl">
                      {sugestii.lista.length ? t("vol.sugestii_in", { n: sugestii.lista.length, s: sugestii.secunde.toFixed(1) })
                                             : t("vol.sugestii_nimic")}
                    </span>
                    <div className="vl-sugestii-rand">
                      {sugestii.lista.map((s, i) => (
                        <button key={i} type="button" className="vl-sugestie" onClick={() => incarcaSugestia(s)}
                                title={`${s.forme.join(" + ")} · ${Math.round(s.amprenta_m2)} m²`}>
                          <Miniatura contur={contur} volume={s.volume.map((v) => v.poly)} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="vl-panza-cadru">
                  {vedere && (
                    <svg ref={svgRef} className={`vl-panza${potScrie ? "" : " vl-doar-citire"}`}
                         viewBox={`${vedere.x} ${vedere.y} ${vedere.w} ${vedere.h}`}
                         preserveAspectRatio="xMidYMid meet"
                         onPointerDown={laApasare} onPointerMove={laMiscare}
                         onDoubleClick={(ev) => { if (!(ev.target as Element).closest("[data-vol]")) incadreaza(); }}
                         onPointerUp={laRidicare} onPointerCancel={laRidicare}>
                      <defs>
                        <pattern id="vl-hasura" width={3} height={3} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                          <line x1={0} y1={0} x2={0} y2={3} stroke="var(--acc)" strokeWidth={0.25} opacity={0.5} />
                        </pattern>
                      </defs>
                      {/* lotul */}
                      <polygon className="vl-lot" points={puncte(contur)} />
                      {/* zona edificabilă, de la motor */}
                      {(est?.zona ?? []).map((inel, i) => (
                        <polygon key={i} className="vl-zona" points={puncte(inel)} fill="url(#vl-hasura)" />))}
                      {/* distanța cerută în jurul fiecărui volum: conturul îngroșat cu ea */}
                      {gap > 0 && volume.map((v) => (
                        <polygon key={`h-${v.id}`} className="vl-halo" points={puncte(v.poly)}
                                 strokeWidth={2 * gap} />))}
                      {/* volumele */}
                      {volume.map((v) => (
                        <g key={v.id} data-vol={v.id}
                           className={`vl-vol${v.id === sel ? " on" : ""}${inAfara.has(v.id) ? " afara" : ""}${preaAproape.has(v.id) ? " aproape" : ""}`}>
                          <polygon points={puncte(v.poly)} />
                          {/* lungimile, pe mijlocul fiecărui braț — nu la mânere, unde s-ar
                              călca cu cotele */}
                          {v.id === sel && etichetele(v).map((e, k) => {
                            const q = laPanza(e.p);
                            return <text key={k} x={q[0]} y={q[1] + fs * 0.35} fontSize={fs} textAnchor="middle">{e.text}</text>;
                          })}
                        </g>
                      ))}
                      {/* cotele celui selectat: vecinul cel mai apropiat și limita lotului */}
                      {selectat && vecinul && vecinul.d > 0 && (
                        <Cota a={vecinul.p} b={vecinul.q} text={`${vecinul.d.toFixed(1)} m`} rau={gap > 0 && vecinul.d < gap - 0.05} fs={fs} />)}
                      {selectat && limita && limita.d > 0 && (
                        <Cota a={limita.p} b={limita.q} text={`${limita.d.toFixed(1)} m`} rau={!limita.inauntru} fs={fs} slab />)}
                      {/* mânerele celui selectat */}
                      {selectat && potScrie && G.manere(selectat).map((m, i) => {
                        const q = laPanza(m.p);
                        return m.fel === "rotire"
                          ? <circle key={i} data-vol={selectat.id} data-maner={i} className="vl-maner rotire" cx={q[0]} cy={q[1]} r={rManer * 1.2} />
                          : <rect key={i} data-vol={selectat.id} data-maner={i} className={`vl-maner ${m.fel}`}
                                  x={q[0] - rManer} y={q[1] - rManer} width={2 * rManer} height={2 * rManer} />;
                      })}
                    </svg>
                  )}
                  <div className="vl-legenda">
                    <span><i className="vl-leg-zona" /> {t("vol.legenda_zona")}</span>
                    <span><i className="vl-leg-halo" /> {t("vol.legenda_halo")}</span>
                    {!volume.length && <span className="note">{t("vol.gol")}</span>}
                  </div>
                </div>

                <div className="vl-cifre" title={t("vol.estimat_titlu")}>
                  <span className="chip c-out">{t("vol.estimat")}</span>
                  <Cifra nume={t("vol.amprenta")} val={est ? `${Math.round(est.amprenta_m2)} m²` : "—"} />
                  <Cifra nume={t("vol.pot")} val={est ? `${(est.pot * 100).toFixed(1)} %` : "—"}
                         sub={est ? t("vol.din_max", { max: `${Math.round(est.pot_max * 100)} %` }) : undefined}
                         rau={!!est && est.pot > est.pot_max + 1e-6} />
                  <Cifra nume={t("vol.cut")} val={est ? est.cut.toFixed(2) : "—"}
                         sub={est ? t("vol.din_max", { max: est.cut_max.toFixed(2) }) : undefined}
                         rau={!!est && est.cut > est.cut_max + 1e-6} />
                  {est && <span className="note">{t("vol.regim", { n: est.niveluri, gap: est.gap_m.toFixed(1) })}</span>}
                  {est && inAfara.size > 0 && <span className="vl-rau">{t("vol.in_afara", { n: inAfara.size })}</span>}
                  {est && (est.perechi_sub_distanta?.length ?? 0) > 0 && (
                    <span className="vl-rau">{t("vol.prea_aproape", { n: est.perechi_sub_distanta.length })}</span>)}
                  {estErr && <span className="vl-rau">{t("vol.estimare_eroare", { motiv: estErr })}</span>}
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

/** Textele de pe brațele unui volum: lungime × lățime pe brațul principal,
 *  lungimea pe fiecare aripă, fiecare la mijlocul brațului lui. */
function etichetele(v: VolumSchita): { p: Pct; text: string }[] {
  const h = v.latime_m / 2, L = v.brate_m[0], s = v.parte;
  // numai lungimea: lățimea e banda modulelor și se vede pe mânerul ei
  const out = [{ p: G.dinReperulVolumului(v, 0, 0), text: `${L.toFixed(1)} m` }];
  const aripa = (e: number, Lk: number) =>
    ({ p: G.dinReperulVolumului(v, e * (L / 2 - h), s * (-h + Lk / 2 + h / 2)), text: `${Lk.toFixed(1)} m` });
  if (v.forma === "L") out.push(aripa(v.capat, v.brate_m[1]));
  if (v.forma === "U" || v.forma === "C") {
    out.push(aripa(-1, v.brate_m[1]));
    out.push(aripa(1, v.brate_m[2] ?? v.brate_m[1]));
  }
  return out;
}

function Cifra({ nume, val, sub, rau }: { nume: string; val: string; sub?: string; rau?: boolean }) {
  return (
    <span className={`vl-cifra${rau ? " rau" : ""}`}>
      <span className="lbl">{nume}</span>
      <b>{val}</b>
      {sub && <span className="note">{sub}</span>}
    </span>
  );
}

/** O cotă între două puncte, cu textul la mijloc. */
function Cota({ a, b, text, rau, fs, slab }: { a: Pct; b: Pct; text: string; rau: boolean; fs: number; slab?: boolean }) {
  const p = laPanza(a), q = laPanza(b);
  return (
    <g className={`vl-cota${rau ? " rau" : ""}${slab ? " slab" : ""}`}>
      <line x1={p[0]} y1={p[1]} x2={q[0]} y2={q[1]} />
      <text x={(p[0] + q[0]) / 2} y={(p[1] + q[1]) / 2 - fs * 0.4} fontSize={fs} textAnchor="middle">{text}</text>
    </g>
  );
}

/** Pictograma unei forme din paletă, 14 px. */
function FormaMica({ forma }: { forma: FormaVolum }) {
  const d = forma === "bara" ? "M1 5h12v4H1z"
          : forma === "L" ? "M1 1h4v8h8v4H1z"
          : forma === "U" ? "M1 1h4v8h4V1h4v12H1z"
          : "M1 1h4v8h4V4h4v9H1z";
  return <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden="true"><path d={d} fill="currentColor" /></svg>;
}

/** O așezare propusă, mică: lotul și volumele ei. */
function Miniatura({ contur, volume }: { contur: Pct[]; volume: Pct[][] }) {
  const b = G.cutie(contur);
  const m = 6;
  return (
    <svg viewBox={`${b.e0 - m} ${-b.n1 - m} ${b.e1 - b.e0 + 2 * m} ${b.n1 - b.n0 + 2 * m}`} preserveAspectRatio="xMidYMid meet">
      <polygon points={puncte(contur)} fill="none" stroke="var(--mut)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      {volume.map((p, i) => <polygon key={i} points={puncte(p)} fill="var(--acc)" opacity={0.75} />)}
    </svg>
  );
}
